import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, ChevronRight } from 'lucide-react'
import { toast } from 'sonner'
import { useDocumentHead } from '@/hooks/use-document-head'
import { useHeaderExtra } from '@/contexts/header.context'
import { evalApiClient } from '@/lib/api/eval.client'
import { AddEvalCaseModal } from '@/components/eval/AddEvalCaseModal'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import type { EvalCase } from '@super/shared-types'

export function EvalCaseDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [evalCase, setEvalCase] = useState<EvalCase | null>(null)
  const [editOpen, setEditOpen] = useState(false)

  useDocumentHead({
    title: evalCase ? `Case · ${evalCase.name}` : 'Case Detail',
  })

  useHeaderExtra(
    <div className="flex items-center gap-1.5 text-sm">
      <Link
        to="/evaluations"
        className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-accent transition-colors"
      >
        <ArrowLeft className="w-4 h-4 text-muted-foreground" />
      </Link>
      <Link
        to="/evaluations"
        className="text-muted-foreground hover:text-foreground transition-colors"
      >
        Cases
      </Link>
      <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/60" />
      <span className="text-foreground">{evalCase?.name ?? '…'}</span>
    </div>,
    [evalCase?.name],
  )

  useEffect(() => {
    if (!id) return
    evalApiClient.getCaseById(id).then(c => {
      if (c) setEvalCase(c)
    })
  }, [id])

  function handleUpdated() {
    if (!id) return
    evalApiClient.getCaseById(id).then(c => {
      if (c) setEvalCase(c)
    })
  }

  return (
    <div className="h-full overflow-y-auto pt-16">
      <div className="p-6 pb-24">
        {!evalCase ? (
          <div className="flex flex-col gap-6 max-w-3xl">
            <div className="flex items-start justify-between">
              <div className="flex flex-col gap-2">
                <Skeleton className="h-7 w-48" />
                <Skeleton className="h-4 w-64" />
              </div>
              <Skeleton className="h-8 w-16" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex flex-col gap-2">
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="h-5 w-28" />
                </div>
              ))}
            </div>
            <div className="flex flex-col gap-2">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-32 w-full" />
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-6 max-w-3xl">
            <div className="flex items-start justify-between">
              <div className="flex flex-col gap-1">
                <h2 className="text-xl font-semibold">{evalCase.name}</h2>
                {evalCase.description && (
                  <p className="text-sm text-muted-foreground">
                    {evalCase.description}
                  </p>
                )}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditOpen(true)}
              >
                Edit
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex flex-col gap-1">
                <span className="text-muted-foreground text-xs uppercase tracking-wide">
                  Prompt type
                </span>
                <Badge variant="outline" className="w-fit">
                  {evalCase.promptType}
                </Badge>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-muted-foreground text-xs uppercase tracking-wide">
                  Version
                </span>
                <span className="font-mono">
                  {evalCase.promptVersion ?? '—'}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-muted-foreground text-xs uppercase tracking-wide">
                  Model
                </span>
                <span className="font-mono">{evalCase.model}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-muted-foreground text-xs uppercase tracking-wide">
                  Company
                </span>
                <span>{evalCase.companyId ?? 'Global'}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-muted-foreground text-xs uppercase tracking-wide">
                  Source
                </span>
                <Badge
                  variant={
                    evalCase.source === 'SESSION' ? 'secondary' : 'outline'
                  }
                  className="w-fit"
                >
                  {evalCase.source}
                </Badge>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-muted-foreground text-xs uppercase tracking-wide">
                  Created
                </span>
                <span>{new Date(evalCase.createdAt).toLocaleString()}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-muted-foreground text-xs uppercase tracking-wide">
                  Updated
                </span>
                <span>{new Date(evalCase.updatedAt).toLocaleString()}</span>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <span className="text-muted-foreground text-xs uppercase tracking-wide">
                Content
              </span>
              <pre className="rounded-md border bg-muted p-4 text-sm whitespace-pre-wrap break-words font-mono">
                {evalCase.content}
              </pre>
            </div>

            {evalCase.inputContext &&
              Object.keys(evalCase.inputContext).length > 0 && (
                <div className="flex flex-col gap-2">
                  <span className="text-muted-foreground text-xs uppercase tracking-wide">
                    Input context
                  </span>
                  <pre className="rounded-md border bg-muted p-4 text-sm font-mono overflow-x-auto">
                    {JSON.stringify(evalCase.inputContext, null, 2)}
                  </pre>
                </div>
              )}

            <div className="flex flex-col gap-2">
              <span className="text-muted-foreground text-xs uppercase tracking-wide">
                Expected output
              </span>
              <pre className="rounded-md border bg-muted p-4 text-sm font-mono overflow-x-auto">
                {JSON.stringify(evalCase.expectedOutput, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>

      {evalCase && (
        <AddEvalCaseModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onCreated={handleUpdated}
          initialCase={evalCase}
          onSuccess={msg => toast.success(msg)}
          onError={msg => toast.error(msg)}
        />
      )}
    </div>
  )
}
